/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { connect, Provider } from 'react-redux';
import PropTypes from 'prop-types';
import { resolveByCountry } from '@codex/helpers';
import { Header as HeaderComponent } from '@codex-applications/header';
import { Header as HeaderBrightworldComponent } from '@codex-applications-brightworld/header';
import styled from 'styled-components';
import { EnvironmentThemeProvider } from '@codex/themes';
import themesBrightworld, { EnvironmentThemeProvider as EnvironmentThemeProviderBrightworld } from '@codex-brightworld/themes';
import urljoin from 'url-join';
import { selectedSuggestion, priceDropNotifications } from '../../../analytics';

import uiLocale from '../../../utils/UILocale.js';
import uiSettings from '../../../utils/UISettings.js';

const HeaderWrapper = styled.div`
  position: relative;
  z-index: 101;
  font-family: 'CurrysSans';
  input {
    width: 100%;
    margin: 0;
  }
  label {
    padding: 0;
    width: auto;
  }
  ul li, ol li {
    margin-bottom: 0;
    a:hover {
      color: #4C12A1;
  }
  }
`;

const accountMenu = {
  orders: {
    link: '/gbuk/s/my-account.html#/orders',
    label: uiLocale.getString('component.my-account-menu.my_account_summary'),
    notificationsLabel: 'updates'
  },
  savedForLater: {
    link: '/gbuk/s/my-account.html#/saved-for-later',
    label: uiLocale.getString('component.my-account-menu.wishlists_section_title'),
    notificationsLabel: 'price drops'
  },
  details: {
    link: '/gbuk/s/my-account.html#/manage-your-details/personal-details',
    label: uiLocale.getString('component.my-account-menu.manage_my_details_title')
  },
  stayInTouch: {
    link: '/gbuk/s/my-account.html#/:preferences',
    label: uiLocale.getString('component.my-account-menu.lets_update_you')
  },
  findOutMore: {
    link: '/gbuk/s/my-account.html#/:questions',
    label: uiLocale.getString('component.my-account-menu.have_any_questions')
  },
  signOut: {
    link: '/gbuk/logout/index.html',
    label: uiLocale.getString('component.my-account-menu.sign_out')
  }
};

const checkBrightworldPossible = (navigationData) =>
  navigationData.catalog && navigationData.catalog.type === 'brightworld catalog' &&
  navigationData.pages && navigationData.pages.type === 'brightworld pages';

const pageHeaderElementQuery = '[data-component="page-header"]';
const HEADER_RENDERED = 'header_rendered';

const triggerHeaderRendered = (el) => {
  const event = new Event(HEADER_RENDERED);
  el.dispatchEvent(event);
};

const NewHeader = (props) => {
  const [storeApiUrl, setStoreApiUrl] = useState(undefined);
  const [isBrightworldPossible, setBrightworldpossible] = useState(undefined);
  useEffect(async () => {
    // for case when App.Settings not initialized yet:
    if (!storeApiUrl) {
      uiSettings.watchChanges('global.store_api_url', 0, setStoreApiUrl);

      // for case when App.Settings already initialized:
      const settingsStoreApiUrl = uiSettings.get('global.store_api_url');
      if (settingsStoreApiUrl) {
        setStoreApiUrl(settingsStoreApiUrl);
      }
      // Check, if we are able to display brightworld header
    } else {
      const navigationApiBrightworldUrl = urljoin(
        storeApiUrl.replace('/store/api', ''),
        '/ff/navigation/navigation',
        '?tenant=currys&brightworld=1'
      );

      let navigationApiResponse;
      try {
        navigationApiResponse = await fetch(navigationApiBrightworldUrl);
      } catch (e) {
        console.error('Unable to load brightworld header navigation.');
      }

      let navigationData;
      if (navigationApiResponse && navigationApiResponse.ok) {
        try {
          navigationData = await navigationApiResponse.json();
        } catch (e) {
          console.error('Unable to parse brightworld header navigation.');
        }
      }

      if (navigationData && checkBrightworldPossible(navigationData)) {
        setBrightworldpossible(true);
      } else {
        setBrightworldpossible(false);
      }
      const pageHeaderElement = document.querySelector(pageHeaderElementQuery);
      triggerHeaderRendered(pageHeaderElement);

    }
  }, [storeApiUrl]);

  const logoVariables = {
    desktopLogoSrc: `${props.imageURL}images/logos/brand-currys-logo.png`,
    desktopLogoWidth: 60,
    desktopLogoHeight: 60,
    handHeldLogoSrc: `${props.imageURL}images/logos/brand-currys-logo.png`,
    handHeldLogoWidth: 32,
    handHeldLogoHeight: 32,
    logoLink: '/',
    logoAlt: 'currys'
  };
  const logoVariablesBrightworld = {
    logoIcon: 'bran_currys_logo',
    logoLink: '/',
    logoAlt: 'currys'
  };
  const searchProductVariables = {
    placeholder: uiLocale.getString('header.menu_search_placeholder'),
    btnTitle: uiLocale.getString('header.menu_searchbtn'),
    searchAction: props.searchURL
  };
  const welcomeMsg = uiLocale.getString('component.my-account-menu.menu_welcome');
  return (
    <HeaderWrapper id={'header'}>
      {isBrightworldPossible === true && (
        <EnvironmentThemeProviderBrightworld>
          <HeaderBrightworldComponent
            logoVariables={logoVariablesBrightworld}
            searchProductVariables={searchProductVariables}
            autocompleteApi={props.autocompleteSettings.termsURL}
            saytApi={props.autocompleteSettings.SAYTURL}
            /* eslint-disable prefer-template */
            storeApi={storeApiUrl}
            theme={themesBrightworld.brightworld}
            navigationApi={storeApiUrl
              ? urljoin(
                storeApiUrl.replace('/store/api', ''),
                '/ff/navigation/navigation',
                '?tenant=currys&brightworld=1'
              )
              : null}
            useOnlyInitialNavigation={!storeApiUrl}
            /* eslint-enable prefer-template */
            basketLink={'/gbuk/o/initorder/basket-confirmation.html'}
            storesLink={'/gbuk/s/find-a-store.html'}
            userLinkSignedIn={'/gbuk/s/my-account.html#/'}
            userLinkSignedOut={'/gbuk/s/authentication.html'}
            userLabelSignedIn={props.userName
              ? welcomeMsg.replace('[NAME]', `, ${props.userName.charAt(0).toUpperCase()}.`)
              : welcomeMsg.replace('[NAME]', '')}
            basketItemsCount={props.cartItemsCount}
            accountMenu={accountMenu}
            userSignedIn={props.isLoggedIn}
            notificationsApi={storeApiUrl && `${storeApiUrl}/account/notifications`}
            onSuggestionSelected={(suggestion) => {
              selectedSuggestion(suggestion.type);

              if (suggestion.type === 'term' && suggestion.title) {
                window.location.href = `/gbuk/search-keywords/xx_xx_xx_xx_xx/${encodeURI(suggestion.title)}/xx-criteria.html`;
              }
            }}
            onNotificationsShown={(notifications) => {
              notifications.forEach((not) => {
                priceDropNotifications(not.currentValue, not.previousValue, not.fupid);
              });
            }}
            fmoRedirectPopupContentUrl={storeApiUrl
              ? urljoin(
                storeApiUrl.replace('/store/api', ''),
                '/ff/fragment-renderers/content-renderer',
                '?fragmentType=dynamic-content',
                '&context={"pageType":"generic","contentArea":"fmoRedirectPopupContent"}'
              )
              : null}
            mobileSiteBaseUrl={`${window.location.protocol}//${window.location.host}/mobile`}
          />
        </EnvironmentThemeProviderBrightworld>
      )}
      {isBrightworldPossible === false && (
        <EnvironmentThemeProvider>
          <HeaderComponent
            logoVariables={logoVariables}
            searchProductVariables={searchProductVariables}
            autocompleteApi={props.autocompleteSettings.termsURL}
            saytApi={props.autocompleteSettings.SAYTURL}
            /* eslint-disable prefer-template */
            navigationApi={storeApiUrl
              ? urljoin(
                storeApiUrl.replace('/store/api', ''),
                '/ff/navigation/navigation',
                '?tenant=currys'
              )
              : null}
            useOnlyInitialNavigation={!storeApiUrl}
            /* eslint-enable prefer-template */
            basketLink={'/gbuk/o/initorder/basket-confirmation.html'}
            storesLink={'/gbuk/s/find-a-store.html'}
            userLinkSignedIn={'/gbuk/s/my-account.html#/'}
            userLinkSignedOut={'/gbuk/s/authentication.html'}
            userLabelSignedIn={props.userName
              ? welcomeMsg.replace('[NAME]', `, ${props.userName.charAt(0).toUpperCase()}.`)
              : welcomeMsg.replace('[NAME]', '')}
            basketItemsCount={props.cartItemsCount}
            accountMenu={accountMenu}
            userSignedIn={props.isLoggedIn}
            notificationsApi={storeApiUrl && `${storeApiUrl}/account/notifications`}
            onSuggestionSelected={(suggestion) => {
              selectedSuggestion(suggestion.type);
            }}
            onNotificationsShown={(notifications) => {
              notifications.forEach((not) => {
                priceDropNotifications(not.currentValue, not.previousValue, not.fupid);
              });
            }}
            fmoRedirectPopupContentUrl={storeApiUrl
              ? urljoin(
                storeApiUrl.replace('/store/api', ''),
                '/ff/fragment-renderers/content-renderer',
                '?fragmentType=dynamic-content',
                '&context={"pageType":"generic","contentArea":"fmoRedirectPopupContent"}'
              )
              : null}
            mobileSiteBaseUrl={`${window.location.protocol}//${window.location.host}/mobile`}
          />
        </EnvironmentThemeProvider>
      )}
    </HeaderWrapper>
  );
};

NewHeader.displayName = 'NewHeader';

NewHeader.propTypes = {
  searchURL: PropTypes.string.isRequired,
  imageURL: PropTypes.string.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  userName: PropTypes.string.isRequired,
  cartItemsCount: PropTypes.number.isRequired,
  autocompleteSettings: PropTypes.shape({
    termsURL: PropTypes.string.isRequired,
    SAYTURL: PropTypes.string.isRequired
  }).isRequired
};

const mapStateToProps = ({ general, userDetails }, ownProps) => ({
  isLoggedIn: ownProps.loggedIn || (Object.keys(userDetails).length > 0 && !userDetails.isGuest),
  userName: userDetails.first_name || '',
  cartItemsCount: general.cartItemsCount
});
const ConnectedHeader = connect(mapStateToProps)(NewHeader);

const hideElementsForGb = (selector) => {
  const element = document.querySelector(`[data-component="${selector}"]`);
  if (element) {
    if (resolveByCountry(true, false)) {
      element.style.display = 'none';
    }
  }
};

export const init = () => {
  const pageHeaderElement = document.querySelector(pageHeaderElementQuery);
  if (!pageHeaderElement) {
    return;
  }
  hideElementsForGb('ssr-navigation-menu-desktop');
  hideElementsForGb('ssr-navigation-menu-touch');
  try {
    const { imageurl, searchurl } = pageHeaderElement.dataset;

    ReactDOM.render(
      <Provider store={window.appState}>
        <ConnectedHeader
          imageURL={imageurl}
          searchURL={searchurl}
          autocompleteSettings={window.DR.autocompleteSettings}
        />
      </Provider>,
      pageHeaderElement
    );

  } catch (e) {
    console.error(e);
  }
};

export default {
  init,
  pageHeaderElementQuery,
  HEADER_RENDERED
};
